// src/pages/bloomterminal/alerts/steps/CustomAlertTypeConfiguration.jsx
import React from 'react';
import Dropdown from '../../../components/Dropdown';
import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';
import ModalPortalSelect from '../components/ModalPortalSelect';

const operatorOptions = [
  { value: 'greaterThan', label: 'Greater than' },
  { value: 'lessThan', label: 'Less than' },
  { value: 'equalTo', label: 'Equal to' },
  { value: 'inBetween', label: 'In between' },
];

const granularityOptions = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

const getUnit = (selectedAlertType) => {
  switch (selectedAlertType) {
    case 'temperature':
      return '°C';
    case 'rain':
      return 'mm';
    case 'price':
      return '$';
    case 'volume':
      return 'lb';
    default:
      return '';
  }
};

const ModalNumberInput = ({ label, value, onChange, placeholder }) => (
  <div className="flex w-full flex-col gap-2">
    <label className="font-['Inter'] text-[14px] text-[#282A29]">{label}</label>
    <input
      type="number"
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      className="h-[42px] w-full rounded-[12px] border border-[#D5D8D6] bg-white px-4 font-['Inter'] text-[16px] text-[#282A29] placeholder:text-[#A0A3A1] focus:border-bloom-green focus:outline-none"
    />
  </div>
);

const CustomAlertTypeConfiguration = ({
  selectedAlertType,
  configuration,
  onConfigurationChange,
  variant = 'default',
}) => {
  const handleOperatorChange = (operator) => {
    onConfigurationChange({
      ...configuration,
      operator,
      value: '',
      minValue: '',
      maxValue: '',
    });
  };

  const handleGranularityChange = (granularity) => {
    onConfigurationChange({ ...configuration, granularity });
  };

  const handleValueChange = (e) => {
    onConfigurationChange({ ...configuration, value: e.target.value });
  };

  const handleMinValueChange = (e) => {
    onConfigurationChange({ ...configuration, minValue: e.target.value });
  };

  const handleMaxValueChange = (e) => {
    onConfigurationChange({ ...configuration, maxValue: e.target.value });
  };

  const { fontSize } = useGlobalFontSize();
  const isInBetweenOperator = configuration.operator === 'inBetween';
  const unit = getUnit(selectedAlertType);
  const isModal = variant === 'modal';

  if (isModal) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ModalPortalSelect
          label="Operator"
          value={configuration.operator}
          placeholder="Select operator"
          options={operatorOptions}
          onSelect={handleOperatorChange}
        />
        <ModalPortalSelect
          label="Granularity"
          value={configuration.granularity}
          placeholder="Select granularity"
          options={granularityOptions}
          onSelect={handleGranularityChange}
        />

        {configuration.operator &&
          (isInBetweenOperator ? (
            <>
              <ModalNumberInput
                label={`Min value ${unit}`.trim()}
                value={configuration.minValue}
                onChange={handleMinValueChange}
                placeholder="Min value"
              />
              <ModalNumberInput
                label={`Max value ${unit}`.trim()}
                value={configuration.maxValue}
                onChange={handleMaxValueChange}
                placeholder="Max value"
              />
            </>
          ) : (
            <ModalNumberInput
              label={`Value ${unit}`.trim()}
              value={configuration.value}
              onChange={handleValueChange}
              placeholder="Enter value"
            />
          ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-12 flex-1">
      <div className="flex flex-col gap-[48px]">
        <div className="flex flex-wrap gap-4 items-start">
          <Dropdown
            label="Operator"
            value={configuration.operator}
            placeholder="Select..."
            options={operatorOptions}
            onSelect={handleOperatorChange}
            className="w-[140px]"
          />
          <Dropdown
            label="Granularity"
            value={configuration.granularity}
            placeholder="Select..."
            options={granularityOptions}
            onSelect={handleGranularityChange}
            className="w-[140px]"
          />

          {configuration.operator &&
            (isInBetweenOperator ? (
              <>
                <div className="flex flex-col gap-[8px] items-start w-[140px]">
                  <label
                    className="font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]"
                    style={{ fontSize }}
                  >
                    Min value {unit}
                  </label>
                  <input
                    type="number"
                    value={configuration.minValue || ''}
                    onChange={handleMinValueChange}
                    placeholder="Min value"
                    className="w-full bg-white dark:bg-[#282a29] border border-bloom-green rounded-[8px] h-[32px] px-[12px] py-[8px] font-['Inter'] font-medium text-[#282A29] dark:text-[#f0f5f2] placeholder:text-[#5d625f] focus:outline-none focus:ring-0"
                    style={{ fontSize }}
                  />
                </div>
                <div className="flex flex-col gap-[8px] items-start w-[140px]">
                  <label
                    className="font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]"
                    style={{ fontSize }}
                  >
                    Max value {unit}
                  </label>
                  <input
                    type="number"
                    value={configuration.maxValue || ''}
                    onChange={handleMaxValueChange}
                    placeholder="Max value"
                    className="w-full bg-white dark:bg-[#282a29] border border-bloom-green rounded-[8px] h-[32px] px-[12px] py-[8px] font-['Inter'] font-medium text-[#282A29] dark:text-[#f0f5f2] placeholder:text-[#5d625f] focus:outline-none focus:ring-0"
                    style={{ fontSize }}
                  />
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-[8px] items-start w-[140px]">
                <label
                  className="font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]"
                  style={{ fontSize }}
                >
                  Value {unit}
                </label>
                <input
                  type="number"
                  value={configuration.value || ''}
                  onChange={handleValueChange}
                  placeholder="Enter value"
                  className="w-full bg-white dark:bg-[#282a29] border border-bloom-green rounded-[8px] h-[32px] px-[12px] py-[8px] font-['Inter'] font-medium text-[#282A29] dark:text-[#f0f5f2] placeholder:text-[#5d625f] focus:outline-none focus:ring-0"
                  style={{ fontSize }}
                />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default CustomAlertTypeConfiguration;
